import React, { useState } from 'react'
import { Textarea } from '../../../lib/ui/textarea'
import { Input } from '../../../lib/ui/input'
import { TrashIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { cn } from '~/lib/utils'
import { useTemplate } from '~/lib/pdf'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import { Button } from '~/lib/ui/button'

export const newKey = () => Math.random().toString(36).substr(2, 9)

export const EditorHeading = ({ className, children }) => {
  const headingClasses = cn(
    'mb-4 text-lg font-medium tracking-[0.014px] text-primary',
    className,
  )

  return <div className={headingClasses}>{children}</div>
}

export const ArrayFields = ({
  items,
  keyPrefix,
  onChange,
  onBlur,
  onDeleteClick = () => null,
}) => {
  const handleOnBlur = () => {
    onBlur()
  }
  return items.map((item, i) => (
    <div key={`${keyPrefix}-${i}`} className="flex flex-row">
      <Textarea
        rows="2"
        value={item}
        name={`${keyPrefix}-arrayField-${i}`}
        aria-label={`${keyPrefix} item ${i}`}
        onChange={(e) => {
          const updatedItems = [...items]
          updatedItems[i] = e.target.value
          onChange(updatedItems)
        }}
        onBlur={handleOnBlur}
      />
      <button
        onClick={() => onDeleteClick(i)}
        className="max-w-min text-[#b9b9b9]"
        aria-label="remove item"
      >
        <TrashIcon className="ml-2 w-8" />
      </button>
    </div>
  ))
}

export const ArrayInputs = ({
  items,
  keyPrefix,
  onChange,
  onBlur,
  onKeyPress = () => null,
  onDeleteClick = () => null,
}) => {
  const handleOnBlur = () => {
    onBlur()
  }
  return items.map((item, i) => (
    <div key={`${keyPrefix}-${i}`} className="flex flex-row">
      <Input
        value={item}
        name={`${keyPrefix}-arrayInput-${i}`}
        onKeyPress={onKeyPress}
        type="text"
        aria-label={`${keyPrefix} item ${i}`}
        onChange={(e) => {
          const updatedItems = [...items]
          updatedItems[i] = e.target.value
          onChange(updatedItems)
        }}
        onBlur={handleOnBlur}
      />
      <button
        onClick={() => onDeleteClick(i)}
        className="max-w-min text-[#b9b9b9]"
        aria-label={`remove item ${i}`}
      >
        <TrashIcon className="ml-2 w-8" />
      </button>
    </div>
  ))
}

export const EditableHeading = ({ title, description, settingName }) => {
  const { useTemplateSetting, isSettingSupported } = useTemplate()
  const [settingValue, setSettingValue] = useTemplateSetting(settingName)

  const [open, setOpen] = useState(false)
  const [heading, setHeading] = useState(settingValue)

  const handleSave = () => {
    setSettingValue(heading)
    setOpen(false)
  }

  if (!isSettingSupported(settingName)) {
    return <EditorHeading className="mb-0">{title}</EditorHeading>
  }

  return (
    <>
      <EditorHeading className="mb-0">{settingValue}</EditorHeading>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button size="sm" variant="link" className="ml-2 rounded-none">
            Edit
            <PencilSquareIcon className="ml-1 w-3.5" />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <DialogDescription>{description}</DialogDescription>
          <div className="flex flex-col gap-4 px-4 pb-4">
            <input
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
            />
            <Button onClick={handleSave}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
